import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CreateNotice.css';
import PageTitle from '../../components/UI/PageTitle';
import { getAnnouncementById, updateAnnouncement } from '../../api/Index';

const EditNotice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    importance: 'medium',
    expiresAt: ''
  });

  useEffect(() => {
    const fetchNoticeData = async () => {
      console.log('EditNotice component mounted');
      console.log('Current location:', location);
      setLoading(true);
      setError(null);
      try {
        const searchParams = new URLSearchParams(location.search);
        const noticeId = searchParams.get('id');
        console.log('Extracted notice ID:', noticeId);
        
        if (!noticeId) {
          console.error('No notice ID found in URL parameters');
          setError('Notice ID not provided');
          return;
        }

        console.log('Calling getAnnouncementById with ID:', noticeId);
        const response = await getAnnouncementById(noticeId);
        console.log('Notice details response:', response);
        
        // Handle backend response structure - it returns { success: true, data: announcement }
        const noticeData = response.data?.data || response.data;
        console.log('Parsed notice data:', noticeData);
        
        if (!noticeData) {
          setError('No notice data received from server');
          return;
        }
        
        setNotice(noticeData);
        
        // Pre-fill form data with real backend data
        const formDataToSet = {
          title: noticeData.title || '',
          content: noticeData.content || noticeData.description || '',
          importance: noticeData.importance || 'medium',
          expiresAt: noticeData.expiresAt ? new Date(noticeData.expiresAt).toISOString().split('T')[0] : ''
        };
        console.log('Setting form data:', formDataToSet);
        setFormData(formDataToSet);
        
      } catch (err) {
        console.error('Error fetching notice details:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);
        setError(err.response?.data?.message || err.response?.data?.msg || err.message || 'Failed to load notice details');
      } finally {
        console.log('Finished loading notice data');
        setLoading(false);
      }
    };

    fetchNoticeData();
  }, [location.search]);

  const handleInputChange = (e) => {
    const updatedFormData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    console.log('Form input changed:', e.target.name, '=', e.target.value);
    console.log('Updated form data:', updatedFormData);
    setFormData(updatedFormData);
  };

  const handleImportanceClick = (importance) => {
    setFormData({
      ...formData,
      importance
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    
    try {
      const searchParams = new URLSearchParams(location.search);
      const noticeId = searchParams.get('id');
      
      if (!noticeId) {
        setError('Notice ID not provided');
        return;
      }

      const updateData = {
        title: formData.title,
        content: formData.content,
        importance: formData.importance,
        expiresAt: formData.expiresAt || null
      };

      const response = await updateAnnouncement(noticeId, updateData);
      console.log('Notice updated successfully:', response.data);
      
      // Check if update was successful
      if (response.data?.success || response.data) {
        // Navigate back to notices page
        navigate('/notices');
      } else {
        setError('Update failed - no confirmation received from server');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.msg || err.message || 'Failed to update notice';
      setError(errorMessage);
      console.error('Error updating notice:', err);
      console.error('Update error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title='Edit Notice' />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading notice details...</p>
          <p style={{ color: '#666', fontSize: '14px' }}>Please wait while we fetch the notice data.</p>
        </div>
      </div>
    );
  }


  return (
    <div>
        <PageTitle title='Edit Notice' />
        <div className="create-notice-container">
            <div className="form-container">
                <form className="create-notice-form" onSubmit={handleSubmit}>

                    <div className="input-group">
                        <label className="input-label">Title *</label>
                        <input
                            type="text"
                            className="input"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter Notice Title"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Notice *</label>
                        <textarea
                            className="text-area"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            placeholder="Enter Notice"
                            required
                        />
                    </div>

                    <div className='importance-group'>
                        <label className="input-label">Importance</label>
                        <div className='importance-label-group'>
                            <button 
                              type="button"
                              className={formData.importance === 'low' ? 'active' : ''}
                              onClick={() => handleImportanceClick('low')}
                            >
                              Low
                            </button>
                            <button 
                              type="button"
                              className={formData.importance === 'medium' ? 'active' : ''}
                              onClick={() => handleImportanceClick('medium')}
                            >
                              Medium
                            </button>
                            <button 
                              type="button"
                              className={formData.importance === 'high' ? 'active' : ''}
                              onClick={() => handleImportanceClick('high')}
                            >
                              High
                            </button>
                        </div>
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Expires At</label>
                        <input
                            type="date"
                            className="date-input"
                            name="expiresAt"
                            value={formData.expiresAt}
                            onChange={handleInputChange}
                        />
                    </div>

                    <button 
                      type="submit" 
                      className='form-button'
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : 'Update Notice'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default EditNotice;